export const ORDERED_GRADES = [
  'K',
  'FIRST',
  'SECOND',
  'THIRD',
  'FOURTH',
  'FIFTH',
  'SIXTH',
  'SEVENTH',
  'EIGHTH',
] as const;

export type GradeValue = (typeof ORDERED_GRADES)[number];

export const LEGACY_MULTIPLE_GRADE = 'MULTIPLE';

export const GRADE_LABELS: Record<GradeValue, string> = {
  K: 'K',
  FIRST: '1st',
  SECOND: '2nd',
  THIRD: '3rd',
  FOURTH: '4th',
  FIFTH: '5th',
  SIXTH: '6th',
  SEVENTH: '7th',
  EIGHTH: '8th',
};

type GradeInput = string | null | undefined;

type GradeSource = {
  grade?: GradeInput;
  grades?: readonly GradeInput[] | null;
};

const gradeOrder = new Map<string, number>(
  ORDERED_GRADES.map((grade, index) => [grade, index])
);

const LEGACY_GRADE_ALIASES: Record<string, GradeValue> = {
  k: 'K',
  kindergarten: 'K',
  '1': 'FIRST',
  '1st': 'FIRST',
  first: 'FIRST',
  '2': 'SECOND',
  '2nd': 'SECOND',
  second: 'SECOND',
  '3': 'THIRD',
  '3rd': 'THIRD',
  third: 'THIRD',
  '4': 'FOURTH',
  '4th': 'FOURTH',
  fourth: 'FOURTH',
  '5': 'FIFTH',
  '5th': 'FIFTH',
  fifth: 'FIFTH',
  '6': 'SIXTH',
  '6th': 'SIXTH',
  sixth: 'SIXTH',
  '7': 'SEVENTH',
  '7th': 'SEVENTH',
  seventh: 'SEVENTH',
  '8': 'EIGHTH',
  '8th': 'EIGHTH',
  eighth: 'EIGHTH',
};

const toGradeArray = (
  input?: GradeInput | readonly GradeInput[] | null
): GradeInput[] => {
  if (Array.isArray(input)) {
    return [...(input as readonly GradeInput[])];
  }

  return input == null ? [] : [input as GradeInput];
};

export const isSelectableGrade = (grade: unknown): grade is GradeValue =>
  typeof grade === 'string' && gradeOrder.has(grade);

const coerceLegacyGrade = (grade?: GradeInput): GradeValue | null => {
  if (isSelectableGrade(grade)) {
    return grade;
  }

  if (typeof grade !== 'string') {
    return null;
  }

  return LEGACY_GRADE_ALIASES[grade.trim().toLowerCase()] ?? null;
};

export const normalizeGrades = (
  input?: GradeInput | readonly GradeInput[] | null
): GradeValue[] => {
  const gradeSet = new Set<GradeValue>();

  for (const grade of toGradeArray(input)) {
    if (isSelectableGrade(grade)) {
      gradeSet.add(grade);
    }
  }

  return ORDERED_GRADES.filter((grade) => gradeSet.has(grade));
};

export const getInvalidGradeValues = (
  input?: GradeInput | readonly GradeInput[] | null
) =>
  toGradeArray(input).filter(
    (grade): grade is string =>
      typeof grade === 'string' && grade.length > 0 && !isSelectableGrade(grade)
  );

export const gradesFromLegacyGrade = (grade?: GradeInput): GradeValue[] => {
  const coercedGrade = coerceLegacyGrade(grade);
  return coercedGrade ? [coercedGrade] : [];
};

export const deriveLegacyGradeMirror = (
  grades?: GradeInput | readonly GradeInput[] | null
): GradeValue | null => normalizeGrades(grades)[0] ?? null;

export const readGrades = (source?: GradeSource | null): GradeValue[] => {
  if (!source) {
    return [];
  }

  if (Array.isArray(source.grades)) {
    const normalizedGrades = normalizeGrades(source.grades);
    if (
      normalizedGrades.length > 0 ||
      source.grades.length > 0 ||
      source.grade == null ||
      source.grade === LEGACY_MULTIPLE_GRADE
    ) {
      return normalizedGrades;
    }
  }

  return gradesFromLegacyGrade(source.grade);
};

export const areGradesEqual = (
  first?: GradeInput | readonly GradeInput[] | null,
  second?: GradeInput | readonly GradeInput[] | null
) => {
  const normalizedFirst = normalizeGrades(first);
  const normalizedSecond = normalizeGrades(second);

  return (
    normalizedFirst.length === normalizedSecond.length &&
    normalizedFirst.every((grade, index) => grade === normalizedSecond[index])
  );
};

export const formatGradeLabels = (
  grades?: GradeInput | readonly GradeInput[] | null,
  fallback = 'Grade pending'
) => {
  const labels = normalizeGrades(grades).map((grade) => GRADE_LABELS[grade]);
  if (labels.length === 0 && typeof grades === 'string' && grades.trim()) {
    return grades.trim();
  }

  return labels.length > 0 ? labels.join(', ') : fallback;
};

export const formatGradeSource = (
  source?: GradeSource | null,
  fallback = 'Grade pending'
) => formatGradeLabels(readGrades(source), fallback);

export const resolveGradeWriteValues = (input: GradeSource) => {
  if (Object.prototype.hasOwnProperty.call(input, 'grades')) {
    const grades = normalizeGrades(input.grades ?? []);

    return {
      grades,
      grade: deriveLegacyGradeMirror(grades),
    };
  }

  if (Object.prototype.hasOwnProperty.call(input, 'grade')) {
    const grades = gradesFromLegacyGrade(input.grade);

    return {
      grades,
      grade: deriveLegacyGradeMirror(grades),
    };
  }

  return {};
};
